import type {
  AssertionEvaluationOutcome,
  AssertionSeverity,
  AssertionType,
  BrowserMode,
  ElementInfo,
} from '@chatsdet/types';
import type { ClickResult } from './click-validation.js';
import type {
  FastDriveScanInput,
  FastDriveScanResult,
} from './playwright-client/fast-drive-scan.js';
import type { LiveSnapshotStats, LiveSnapshotExecutorEvent } from './live-snapshot-stats.js';
import type {
  ScreencastOptions,
  ScreencastFrameHandler,
  VideoEncodingCallbacks,
} from './cdp-screencast.js';
import type { CursorOverlay } from './cursor-overlay.js';
import type { AutonomousCursor } from './autonomous-cursor.js';
import type {
  CapturedConsoleMessage,
  CapturedDownload,
  CapturedNetworkRequest,
  CapturedToast,
} from './playwright-client/internal-types.js';
import type { BrowserAuthDiagnostics } from './auth-diagnostics.js';

/**
 * Default viewport dimensions for browser automation.
 * 1280x800 is the recommended resolution for AI computer-use models
 * (optimized for vision accuracy, token efficiency, and click precision).
 */
export const DEFAULT_VIEWPORT = { width: 1280, height: 800 } as const;

/**
 * Structured result of running authored Playwright code.
 *
 * Deliberately excludes an evaluation label and error text. Consumers derive
 * classification from these ownership signals so authored exception messages
 * cannot impersonate browser infrastructure.
 */
export type RunCodeExecutionSignals =
  | { ok: true; aborted: false; pageTornDown: false }
  | { ok: false; aborted: boolean; pageTornDown: boolean };

/**
 * Recovery information after a browser crash.
 * Consumed once by the next tool call to notify the agent.
 */
/**
 * Result of probing what element is at a given pixel coordinate.
 */
export interface ProbeResult {
  /** Matched ref from the current accessibility snapshot */
  ref?: string;
  /** Derived label for the element */
  label?: string;
  /** Element bounding box */
  boundingBox?: { x: number; y: number; width: number; height: number };
  /** Raw element metadata from DOM */
  elementInfo: {
    tagName: string;
    id?: string;
    className?: string;
    ariaLabel?: string;
    textContent?: string;
    role?: string;
  };
}

export interface RecoveryInfo {
  /** Type of crash that occurred */
  crashType: 'page_crash' | 'context_closed' | 'browser_disconnect';
  /** URL that was restored to after recovery */
  recoveredUrl: string;
  /** Whether navigation to the restored URL succeeded */
  urlRestored: boolean;
  /** ISO timestamp of when recovery completed */
  timestamp: string;
  /** Whether video was recording before crash */
  wasRecording?: boolean;
  /** Video directory path (for salvaging partial video) */
  videoDir?: string;
}

/**
 * Method for capturing browser storage state.
 *
 * - 'playwright': Uses Playwright's native storageState() API.
 *   Captures both cookies AND localStorage. More complete.
 *
 * - 'evaluate': Uses page.evaluate() to extract localStorage only.
 *   Cannot capture cookies. Use as fallback if 'playwright' fails.
 */
export type StorageStateMethod = 'playwright' | 'evaluate';

export type MCPMediaContent = { data: string; mimeType: string };

/**
 * Structured response when content includes media.
 * Text-only responses are returned as plain strings for backward compatibility.
 */
export type MCPContentResult = {
  text: string;
  media?: MCPMediaContent[];
  /** @deprecated Use `media`; retained for compatibility with older callers. */
  images?: MCPMediaContent[];
};

/**
 * Scroll-specific return shape. `scroll()` and `scrollAtCoordinates()` return
 * `string | ScrollResult` instead of `string | MCPContentResult` so the scroll
 * metadata stays scoped to those methods. `scrollInfo` is optional — remote or
 * legacy client implementations may omit it and still satisfy the interface;
 * the `DirectPlaywrightClient` always populates it. Every `MCPContentResult`
 * is a valid `ScrollResult` (with `scrollInfo` undefined).
 */
export type ScrollResult = MCPContentResult & {
  scrollInfo?: ScrollInfo;
};

/**
 * Structured scroll outcome emitted by `scroll()` and `scrollAtCoordinates()`.
 * Consumed by the executor to decide whether to return the full aria snapshot
 * (moved=true) or a compact no-movement hint (moved=false).
 */
export type ScrollInfo = {
  moved: boolean;
  direction: 'up' | 'down' | 'left' | 'right';
  before: { x: number; y: number };
  after: { x: number; y: number };
  /** Optional geometry of the scroll target (page or container). */
  geometry?: {
    scrollHeight?: number;
    clientHeight?: number;
    scrollWidth?: number;
    clientWidth?: number;
    maxScrollY?: number;
    maxScrollX?: number;
  };
  atTop: boolean;
  atBottom: boolean;
  atLeft: boolean;
  atRight: boolean;
  withinRef?: string;
  toRef?: string;
};

/**
 * Snapshot mode controls what data is returned from browser_snapshot.
 * - 'auto': Return tree + screenshot only when the tool layer decides one is useful
 * - 'both': Return accessibility tree + screenshot
 * - 'screenshot': Return only screenshot, no accessibility tree (for visual-first mode)
 * - 'tree': Return only accessibility tree, no screenshot (for DOM-only inspection)
 * - 'tree-refless': Ref-less accessibility tree (fast-drive mode) — captured via
 *   `ariaSnapshot()` without ai-mode ref-stamping and WITHOUT the per-ref
 *   augmentation pass. Cheaper; the agent drives via Playwright code + semantic
 *   locators rather than refs.
 */
export type SnapshotMode = 'auto' | 'both' | 'screenshot' | 'tree' | 'tree-refless';

/**
 * Common options for browser client methods.
 */
export interface BrowserClientOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

export type BrowserDomSignals = {
  rev: number;
  dialogOpenTs: number;
  dialogName: string;
};

export type BrowserConsoleErrorSummary = {
  count: number;
  first?: string;
};

/**
 * Minimal interface for a file chooser dialog — avoids importing Playwright types directly.
 */
export interface FileChooserLike {
  setFiles(files: string | string[]): Promise<void>;
}

/**
 * Extended options for file upload operations.
 */
export interface FileUploadOptions extends BrowserClientOptions {
  /** Element ref to click — triggers filechooser natively, falls back to setInputFiles. */
  ref?: string;
  /** Pre-prepared filechooser promise from look-ahead (cache replay). */
  preparedFileChooser?: Promise<FileChooserLike>;
  /**
   * Parallel-indexed to `paths` (same length; `undefined` entries mean "no
   * remote fetch needed for this path"). For a remote backend (microVM guest,
   * customer-VPC EC2 host) that cannot read a worker-local file path directly,
   * each present entry is a presigned GET URL the peer downloads to its OWN
   * local filesystem — materializing a fresh guest-local path — before
   * calling its local file-input API with that path instead of the original.
   * Local implementations (`DirectPlaywrightClient`) ignore this entirely;
   * they already have direct filesystem access to `paths`.
   */
  remoteFetchFiles?: Array<{ url: string; filename: string } | undefined>;
}

/**
 * Options for video recording.
 * Video is configured at context creation time - cannot be started/stopped dynamically.
 *
 * @deprecated No callers configure video via this object form anymore;
 * `recordVideo: boolean` is the only supported form. Retained as an exported
 * type for downstream consumers that may still import it.
 */
export interface VideoRecordingOptions {
  /** Directory to save videos. If not provided, uses a temp directory */
  dir?: string;
  /** Video resolution. Defaults to viewport size */
  size?: { width: number; height: number };
}

/**
 * A presigned S3 PUT the worker mints just-in-time and passes as a command
 * argument, so a guest with no channel back to the worker (the microVM's WS
 * protocol is strictly request/response) can upload an artifact it produced
 * without the bytes ever crossing the microVM's bandwidth-capped inbound
 * endpoint. Local implementations (`DirectPlaywrightClient`) ignore this —
 * they already write the artifact to a path the worker can read directly.
 */
export interface ArtifactUploadTarget {
  url: string;
  key: string;
}

/**
 * Result of video recording.
 */
export interface VideoRecordingResult {
  /**
   * Path to the video file. When `s3Key` is set (a MicroVM guest uploaded the
   * artifact itself), this is a path on the GUEST's filesystem — the worker
   * cannot open it and must use `s3Key` instead.
   */
  videoPath: string;
  /** Whether recording was enabled and a video was produced */
  recorded: boolean;
  /**
   * Set when `saveVideo({ upload })` was passed an upload target and the
   * implementation uploaded the artifact itself. When present, `videoPath` is
   * not locally readable and callers must use this S3 key instead.
   */
  s3Key?: string;
  /** Uploaded byte size — only meaningful alongside `s3Key`. */
  sizeBytes?: number;
  /**
   * Recording duration in milliseconds as reported by the encoder. Only
   * meaningful alongside `s3Key` — lets the caller skip a worker-side ffprobe
   * it has no local file to run.
   */
  durationMs?: number;
  /** Screencast encoder telemetry */
  screencastTelemetry?: {
    /** Duration from monotonic clock (performance.now() delta) */
    encoderDurationMs: number;
    /** Frames received from page.screencast onFrame callbacks */
    receivedFrameCount: number;
    /** Frames actually written to ffmpeg stdin */
    writtenFrameCount: number;
    /** Wall-clock ms when ffmpeg spawn was initiated. */
    encoderCreatedAtMs: number | null;
    /** Wall-clock ms of the first frame accepted by the encoder. */
    firstFrameWallClockMs: number | null;
    /** Frames dropped before ffmpeg stdin became writable. */
    framesDroppedBeforeReady: number;
    /** Largest inter-frame gap on accepted frames (ms). */
    maxInterFrameGapMs: number;
    /**
     * Number of times CDP screencast was cold-started or restarted because the
     * subscribed page changed. Preserves prior `screencastStartCount` semantics.
     */
    screencastStartCount: number;
    /**
     * Number of times CDP screencast was restarted because the merged subscriber
     * params (quality / max-dims / everyNthFrame) changed. Surfaces param-thrash
     * separately from page-swap churn.
     */
    paramRestartCount: number;
  };
}

/**
 * Options for stealth mode (anti-bot detection evasion).
 * Only supported by DirectPlaywrightClient.
 */
export interface StealthOptions {
  enabled: boolean;
}

/**
 * Options for loading Chrome extensions.
 * Only supported by DirectPlaywrightClient.
 *
 * Note: Extensions require `launchPersistentContext` which changes browser lifecycle.
 * A user data directory is created (or specified) to store extension state.
 */
export interface ExtensionOptions {
  /** Paths to extension directories to load */
  paths: string[];
  /** User data directory (auto-generated if not provided) */
  userDataDir?: string;
  /** Keep user data dir after disconnect (default: false) */
  persistUserData?: boolean;
}

/**
 * Options for visual highlighting of interactions in video recordings.
 * Only supported by DirectPlaywrightClient when video recording is enabled.
 */
export interface InteractionHighlightOptions {
  /** Enable interaction highlighting */
  enabled: boolean;
  /** Animation duration in ms (default: 150) */
  duration?: number;
}

/**
 * Options for the animated cursor overlay in video recordings.
 * Shows a visible cursor that glides to each action target before interactions.
 */
export interface CursorOverlayOptions {
  enabled?: boolean;
  /** Cursor travel speed in pixels per millisecond (default: 1.5) */
  speedPxPerMs?: number;
  /** Minimum animation duration in ms (default: 100) */
  minDurationMs?: number;
  /** Maximum animation duration in ms (default: 600) */
  maxDurationMs?: number;
}

/**
 * Options for connecting to the browser.
 */
export interface BrowserConnectOptions {
  storageStatePath?: string;
  /**
   * Fail browser setup if the supplied storage state cannot be read or its
   * IndexedDB extension cannot be restored completely. Suite snapshots and
   * post-login recovery state set this because continuing would violate the
   * authenticated-session invariant.
   */
  storageStateRequired?: boolean;
  /**
   * Inline storage state JSON (Playwright StorageState format: cookies, origins,
   * localStorage). Used for remote browser clients where file paths aren't
   * accessible — the same reason {@link IBrowserClient.swapContext} takes one.
   * Takes precedence over `storageStatePath` when both are provided.
   *
   * `storageStatePath` names a file on the *caller's* filesystem, which is
   * meaningless to a browser running on another machine (a MicroVM guest, the
   * customer-VPC EC2 browser, the CLI's local browser). `RemoteBrowserClient`
   * therefore inlines the file's contents into this field before sending, so
   * callers can keep passing a path and stay unaware of where the browser runs.
   */
  storageState?: unknown;
  browserMode?: BrowserMode;
  /**
   * Connect to an existing Chrome instance via Chrome DevTools Protocol.
   * When provided, the browser is not launched — instead we connect to this endpoint.
   */
  cdpUrl?: string;
  /**
   * Custom viewport size. Defaults to 1280x800.
   * Note: Only supported by DirectPlaywrightClient.
   */
  viewport?: { width: number; height: number };
  /**
   * Enable video recording. Must be configured at connect time.
   * Note: Only supported by DirectPlaywrightClient.
   */
  recordVideo?: boolean;
  /**
   * Enable stealth mode for anti-bot detection evasion.
   * Uses playwright-extra with puppeteer-extra-plugin-stealth (17 evasion modules).
   * Pass `true` for default options or an object for custom settings.
   * Note: Only supported by DirectPlaywrightClient.
   */
  stealth?: boolean | StealthOptions;
  /**
   * Load Chrome extensions into the browser.
   * Requires using launchPersistentContext internally.
   * Note: Only supported by DirectPlaywrightClient.
   */
  extensions?: ExtensionOptions;
  /**
   * Enable visual highlighting of interactions for video recordings.
   * Shows animated highlights before clicks, typing, hovers, and drags.
   * Defaults to `true` when recordVideo is enabled.
   * Pass `false` to disable, or an object for custom settings.
   * Note: Only effective when recordVideo is also enabled.
   * Note: Only supported by DirectPlaywrightClient.
   */
  highlightInteractions?: boolean | InteractionHighlightOptions;
  /**
   * Enable animated cursor overlay for video recordings.
   * Shows a visible cursor that glides to each action target before interactions.
   * Defaults to `true` when both recordVideo and highlightInteractions are enabled.
   * Note: Only effective when recordVideo is also enabled.
   * Note: Only supported by DirectPlaywrightClient.
   */
  cursorOverlay?: boolean | CursorOverlayOptions;
  /**
   * Custom HTTP headers to send with every request.
   * Useful for bypassing bot detection (e.g., custom User-Agent, Accept-Language).
   * Note: Only supported by DirectPlaywrightClient.
   */
  extraHTTPHeaders?: Record<string, string>;
  /**
   * HTTP Basic Authentication credentials for browser-level auth popups.
   * Passed to Playwright via browser.newContext({ httpCredentials }).
   */
  httpCredentials?: { username: string; password: string };
  /**
   * Proxy server for routing all browser traffic.
   * Useful for IP allowlisting (route through VPC) or residential proxies (bypass bot detection).
   * Example: { server: 'socks5://localhost:1080' }
   * Note: Only supported by DirectPlaywrightClient.
   */
  proxy?: BrowserProxyConfig;
}

/**
 * Proxy configuration for routing browser traffic through a specific server.
 * Matches Playwright's native `LaunchOptions.proxy` shape.
 */
export interface BrowserProxyConfig {
  server: string;
  bypass?: string;
  username?: string;
  password?: string;
}

/** Stable within-context selector accepted by tab close/select operations. */
export type BrowserTabSelector = number | { index?: number; handle?: number; match?: string };

/** Entry returned by `tabs('list')`. Handles are stable only within one context. */
export interface BrowserTabInfo {
  index: number;
  handle: number;
  url: string;
  title: string;
  active: boolean;
}

/**
 * Unified browser automation client interface.
 * Implemented by `DirectPlaywrightClient`.
 */
export interface IBrowserClient {
  // ================ LIFECYCLE ================

  /**
   * Connect to or launch a browser instance.
   * @param arg - Storage state path (string) or connection options object
   */
  connect(arg?: string | BrowserConnectOptions): Promise<void>;

  /**
   * Disconnect from the browser and cleanup resources.
   */
  disconnect(): Promise<void>;

  // ================ CONTEXT MANAGEMENT ================

  /**
   * Swap to a new browser context within the same browser process.
   *
   * This enables switching authenticated sessions mid-execution by closing the
   * current context and creating a new one with a different storage state.
   *
   * Notes:
   * - Only supported by DirectPlaywrightClient in non-extensions mode.
   * - Implementations may throw if unsupported.
   * - If video recording is enabled, the current video is saved before swap.
   */
  swapContext?(options: {
    storageStatePath?: string;
    /** See {@link BrowserConnectOptions.storageStateRequired}. */
    storageStateRequired?: boolean;
    /**
     * Inline storage state JSON (Playwright StorageState format: cookies, origins, localStorage).
     * Used for remote browser clients where file paths aren't accessible.
     * Takes precedence over storageStatePath when both are provided.
     */
    storageState?: unknown;
    recordVideo?: boolean;
    /** Callback invoked with the saved video path before context is closed */
    onVideoSaved?: (result: VideoRecordingResult) => void | Promise<void>;
    /** Extra HTTP headers to apply to the new context (merged with existing headers from connect) */
    extraHTTPHeaders?: Record<string, string>;
    /** HTTP Basic Authentication credentials for browser-level auth popups */
    httpCredentials?: { username: string; password: string };
  }): Promise<void>;

  /**
   * Whether this client supports swapContext().
   */
  supportsContextSwap?(): boolean;

  /**
   * Record the authenticated storage state to use when the browser
   * context is rebuilt by crash/wedge recovery.
   *
   * The "deferred+login" auth path logs in on the live context without a
   * context swap, so the authenticated cookies live only in the running
   * context — the connect-time storageStatePath is still the pre-login state.
   * If recovery (e.g. after a post-action snapshot timeout forces a pool-lease
   * swap) rebuilds from that stale path, the new context comes up logged out
   * and the run hits an auth wall. Callers invoke this after a successful login
   * so recovery restores the authenticated session instead.
   */
  setAuthenticatedStorageStatePath?(storageStatePath: string): void;

  /**
   * Monotonic count of completed crash/wedge recoveries. Unlike
   * consumeRecoveryInfo() this is never cleared — compare it across a step to
   * detect whether the browser context was rebuilt mid-step.
   */
  getRecoveryGeneration?(): number;

  /**
   * Return a bounded, secret-free auth/lifecycle incident buffer. Implemented
   * by direct Playwright clients and forwarded by remote transports.
   */
  getAuthDiagnostics?(): Promise<BrowserAuthDiagnostics>;

  // ================ NAVIGATION ================

  /**
   * Navigate to a URL.
   * @returns MCP-formatted response with page snapshot
   */
  navigate(url: string, opts?: BrowserClientOptions): Promise<string>;

  /**
   * Navigate back to the previous page.
   * @returns MCP-formatted response with page snapshot
   */
  navigateBack(opts?: BrowserClientOptions): Promise<string>;

  // ================ PAGE INSPECTION ================

  /**
   * Get an accessibility snapshot of the current page.
   * Returns YAML-like text with element refs (e.g., [ref=e1]).
   * Optionally includes a downsampled screenshot when includeScreenshot is true.
   *
   * @param opts.mode - What to return: 'auto', 'both', 'screenshot', or 'tree'
   * @param opts.includeScreenshot - DEPRECATED: Use mode instead. When true, equivalent to mode:'both'
   * @param opts.depth - Max tree depth to return (default: unlimited)
   * @param opts.filterToInteractive - Only return interactive elements (buttons, links, inputs)
   */
  snapshot(
    opts?: BrowserClientOptions & {
      mode?: SnapshotMode;
      includeScreenshot?: boolean;
      depth?: number;
      filterToInteractive?: boolean;
      /** Max dimension for image downsampling (default: 1280) */
      maxImageDimension?: number;
      /** Ref-less mode only: OR-matched, case-insensitive filter on role+name+scope. */
      search?: string;
    }
  ): Promise<string | MCPContentResult>;

  /**
   * Return the raw YAML from the most recent snapshot, if available.
   * This avoids re-snapshotting and keeps refs stable for selector derivation.
   */
  getLastSnapshotYaml?(): string | null;

  /**
   * Get the last snapshot YAML if it was captured within maxAgeMs milliseconds.
   * Returns null if no snapshot exists or it's stale.
   */
  getLastSnapshotIfFresh?(maxAgeMs: number): string | null;

  /**
   * Enable/disable the live-snapshot (warm + minted) path. When disabled,
   * snapshot() behaves exactly as the legacy v1 path. See live-snapshot.ts.
   */
  setLiveSnapshotEnabled?(enabled: boolean): void;

  /**
   * On a live-snapshot freshness miss, return the raw substrate immediately and
   * augment in the background instead of blocking. Default off → block.
   */
  setLiveSnapshotRawOnMiss?(rawOnMiss: boolean): void;

  /**
   * Enable ref-less (fast-drive) mode. When on, navigate()/navigateBack() skip
   * the forced post-navigation snapshot entirely (the ref-less agent re-perceives
   * with its own ref-less snapshot / run_code observe on its next turn), and
   * warmSnapshot() becomes a no-op. Both captures are perception work the ref-less
   * loop just repeats. Set once per run from the tool-set variant. Off (default)
   * preserves the v1 augmented path. Optional — clients that don't implement it
   * keep augmenting.
   */
  setReflessSnapshots?(refless: boolean): void;

  /**
   * Return the warm snapshot YAML if it's still valid for the live page (URL +
   * DOM revision unchanged since minting), else null — without capturing. Used
   * to reuse the warm snapshot as a diff baseline. Returns null when
   * live-snapshot is disabled.
   */
  getReusableSnapshotYaml?(): Promise<string | null>;

  /**
   * Fire-and-forget background refresh of the warm snapshot during idle time.
   * No-op when live-snapshot is disabled.
   */
  warmSnapshot?(opts?: { quietMs?: number; maxWaitMs?: number; signal?: AbortSignal }): void;

  // ── Fast-drive observation signals ───────────────────────────────────────
  // Cheap signals the fast-drive observation wrapper reads after a `run_code`
  // action so the agent can decide whether to follow up with a snapshot.

  /** Last RAW ref-less substrate YAML captured (fast-drive diff baseline), or '' if none. */
  getReflessYaml?(): string;

  /** Capture a fresh ref-less substrate YAML (stabilizes first) and update the baseline. */
  captureReflessYaml?(opts?: BrowserClientOptions): Promise<string>;

  /**
   * Read `window.__domRevision` (monotonic mutation counter); 0 if unavailable.
   * Superseded by `getDomSignals` (which returns this plus the dialog stamp in
   * one evaluate); retained as the fallback for any client that implements only
   * the rev counter. `BrowserToolExecutor.runCode` prefers `getDomSignals`.
   */
  getDomRevision?(opts?: BrowserClientOptions): Promise<number>;

  /**
   * One-evaluate read of the in-page fast-drive signals: the DOM-revision
   * counter plus the last dialog/modal-open stamp (compare the stamp before vs
   * after a block to detect "a modal opened"). All defaults are zero/empty when
   * the page is navigating/closed.
   */
  getDomSignals?(opts?: BrowserClientOptions): Promise<BrowserDomSignals>;

  /**
   * `browser_scan` — bounded, read-only DOM traversal that finds ONE precise,
   * verified, durable action locator for a text `query` (+ optional
   * `targetRole`/`relation` when the action target is a descendant). Returns a
   * status-tagged triage result that always tells the model its next move; never
   * throws. See {@link module:playwright-client/fast-drive-scan}.
   */
  scan?(input: FastDriveScanInput, opts?: BrowserClientOptions): Promise<FastDriveScanResult>;

  /** URLs of every open page/tab in the current context (in-memory; '' for an unreadable page). */
  getOpenPageUrls?(): string[];

  /**
   * Open pages/tabs with their durable within-run `handle` (the same id scheme as
   * `tabs('list')`). Lets observation annotate a newly opened tab with an actionable
   * reference the agent can pass back to `browser_tabs`. Optional — only clients that
   * assign stable page ids implement it.
   */
  getOpenPageHandles?(): Array<{ handle: number; url: string }>;

  /** Errors logged to the console at/after `sinceMs` (Date.now()) — count + first message text. */
  getConsoleErrorSummarySince?(sinceMs: number): BrowserConsoleErrorSummary;

  /** Total captured network requests so far — a before/after delta ≈ requests an action triggered. */
  getNetworkRequestCount?(): number;

  /**
   * Record an executor-layer LiveSnapshot efficacy event (reuse hit/miss,
   * capture timing, screenshot decision). No-op when live-snapshot is disabled.
   */
  recordLiveSnapshotEvent?(ev: LiveSnapshotExecutorEvent): void;

  /**
   * Drain (snapshot + reset) the per-step LiveSnapshot efficacy stats. Returns
   * null when nothing was recorded. Called once per step by the agent.
   */
  drainLiveSnapshotStats?(): LiveSnapshotStats | null;

  /**
   * Take a screenshot of the page or a specific element.
   * @returns Base64 data URL or null on failure
   */
  takeScreenshot(opts?: {
    fullPage?: boolean;
    type?: 'png' | 'jpeg';
    element?: string;
    ref?: string;
    /** Clip to a viewport region (page screenshots only — ignored when `ref` is set). */
    clip?: { x: number; y: number; width: number; height: number };
    label?: string;
    signal?: AbortSignal;
    timeoutMs?: number;
  }): Promise<string | null>;

  /**
   * Evaluate JavaScript in the page context.
   * @param fn - JavaScript function as a string (e.g., "() => document.title")
   */
  evaluate<T = unknown>(
    fn: string,
    opts?: { label?: string; ref?: string; signal?: AbortSignal; timeoutMs?: number }
  ): Promise<T>;

  /**
   * Run a Playwright code snippet with access to the page object.
   * @param code - Playwright code (e.g., "await page.click('button')")
   */
  runCode(
    code: string,
    opts?: BrowserClientOptions & {
      /**
       * Named values injected as in-scope identifiers in the executed code
       * (alongside `page`/`fetch`). The tool layer uses this to expose
       * `vars`/`macro`/`attach` for test data & attachments WITHOUT substituting
       * untrusted values into the code string (which would allow injection).
       */
      bindings?: Record<string, unknown>;
      /**
       * Reports the structured outcome synchronously before the formatted
       * response returns. `runCode` formats failures as a normal `Error:` /
       * `[TOOL_TIMEOUT]` result rather than throwing, so callers cannot infer
       * success from the return value.
       */
      onOutcome?: (signals: RunCodeExecutionSignals) => void;
    }
  ): Promise<unknown>;

  /**
   * Get console messages captured since page load.
   * @param opts.onlyErrors - Filter to error messages only
   */
  consoleMessages(opts?: {
    onlyErrors?: boolean;
    /** Wall-clock ms cutoff — by default only messages with `timestampMs >= sinceMs` are returned. */
    sinceMs?: number;
    /** If true, include messages captured before `sinceMs` (tagged as pre-step). */
    includePreStep?: boolean;
    signal?: AbortSignal;
    timeoutMs?: number;
  }): Promise<string>;

  /**
   * Get network requests made since page load.
   */
  networkRequests(opts?: BrowserClientOptions): Promise<string>;

  // ================ INTERACTION ================

  /**
   * Click an element by ref.
   * @param ref - Element reference from snapshot (e.g., "e1")
   * @param elementDesc - Human-readable description for logging
   */
  click(
    ref: string,
    elementDesc?: string,
    opts?: {
      doubleClick?: boolean;
      button?: string;
      modifiers?: string[];
      signal?: AbortSignal;
      timeoutMs?: number;
    }
  ): Promise<ClickResult>;

  /**
   * Click at specific coordinates (vision mode).
   * Returns info about the clicked element if capture succeeds.
   */
  clickAtCoordinates(
    x: number,
    y: number,
    elementDesc: string,
    opts?: {
      signal?: AbortSignal;
      timeoutMs?: number;
      doubleClick?: boolean;
      modifiers?: string[];
    }
  ): Promise<{
    clickedElement?: {
      tagName: string;
      id?: string;
      className?: string;
      textContent?: string;
      role?: string;
      ariaLabel?: string;
    };
  }>;

  /**
   * Move mouse to coordinates without clicking (vision mode).
   */
  moveToCoordinates(
    x: number,
    y: number,
    elementDesc: string,
    opts?: BrowserClientOptions
  ): Promise<void>;

  /**
   * Drag from start to end coordinates (vision mode).
   */
  dragCoordinates(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    elementDesc: string,
    opts?: BrowserClientOptions
  ): Promise<void>;

  /**
   * Hover over an element by ref.
   */
  hover(ref: string, elementDesc?: string, opts?: BrowserClientOptions): Promise<void>;

  /**
   * Drag from one element to another.
   */
  drag(
    startRef: string,
    startElement: string,
    endRef: string,
    endElement: string,
    opts?: BrowserClientOptions
  ): Promise<void>;

  /**
   * Type text into an element.
   * @param ref - Element reference from snapshot
   * @param text - Text to type
   * @param elementDesc - Human-readable description
   * @param submit - Press Enter after typing
   */
  type(
    ref: string,
    text: string,
    elementDesc?: string,
    submit?: boolean,
    opts?: { delay?: number; signal?: AbortSignal; timeoutMs?: number }
  ): Promise<void>;

  /**
   * Press a keyboard key.
   * @param key - Key name (e.g., "Enter", "ArrowDown", "a")
   */
  pressKey(key: string, opts?: BrowserClientOptions): Promise<void>;

  /**
   * Fill multiple form fields at once.
   */
  fillForm(
    fields: Array<{ ref: string; element?: string; value: string }>,
    opts?: BrowserClientOptions
  ): Promise<void>;

  /**
   * Select an option in a dropdown.
   */
  selectOption(
    ref: string,
    value: string,
    elementDesc?: string,
    opts?: BrowserClientOptions
  ): Promise<void>;

  /**
   * Upload files to a file input.
   *
   * Three modes (tried in order when ref is provided):
   * 1. Native filechooser: click ref → intercept filechooser → setFiles (handles custom upload UIs)
   * 2. setInputFiles on nested input[type="file"] within ref element
   * 3. setInputFiles on first input[type="file"] on page
   *
   * When preparedFileChooser is provided (cache replay), resolves it directly.
   *
   * @param paths - Array of absolute file paths
   * @param opts - Options including optional ref and preparedFileChooser
   */
  fileUpload(paths: string[], opts?: FileUploadOptions): Promise<void>;

  /**
   * Scroll the page or within an element.
   *
   * Two modes:
   * 1. **Scroll by amount**: Use `direction` + `amount` to scroll the page (or within `withinRef`)
   * 2. **Scroll to element**: Use `toRef` to scroll so that element is centered in viewport
   *
   * @param direction - 'up' | 'down' | 'left' | 'right' (ignored if toRef provided)
   * @param amount - Scroll intensity 1-10, default 3. Each unit ~120px. (ignored if toRef provided)
   * @param withinRef - Scroll within this scrollable element instead of the page
   * @param toRef - Scroll the page so this element is centered vertically in the viewport
   * @returns Fresh snapshot with screenshot after scrolling
   */
  scroll(
    direction: 'up' | 'down' | 'left' | 'right',
    amount?: number,
    withinRef?: string,
    toRef?: string,
    opts?: BrowserClientOptions
  ): Promise<string | ScrollResult>;

  /**
   * Scroll at specific coordinates using mouse wheel simulation.
   * Moves mouse to (x, y) and dispatches wheel events, mimicking real user scrolling.
   * The browser automatically routes scroll events to the appropriate scrollable element.
   *
   * @param x - X coordinate for scroll origin
   * @param y - Y coordinate for scroll origin
   * @param direction - 'up' | 'down' | 'left' | 'right'
   * @param amount - Scroll intensity 1-10, default 3. Each unit ~120px.
   * @returns Fresh snapshot with screenshot after scrolling
   */
  scrollAtCoordinates(
    x: number,
    y: number,
    direction: 'up' | 'down' | 'left' | 'right',
    amount?: number,
    opts?: BrowserClientOptions
  ): Promise<string | ScrollResult>;

  // ================ DIALOGS ================

  /**
   * Handle a browser dialog (alert, confirm, prompt).
   * @param action - 'accept' or 'dismiss'
   * @param promptText - Text to enter for prompt dialogs
   */
  handleDialog(
    action: 'accept' | 'dismiss',
    promptText?: string,
    opts?: BrowserClientOptions
  ): Promise<void>;

  /**
   * Pre-register the action to take on the next native browser dialog.
   * Used by cache replay to coordinate with the global dialog handler
   * instead of registering a competing page.once('dialog') listener.
   */
  setNextDialogAction?(action: 'accept' | 'dismiss', promptText?: string): void;

  // ================ WAITING ================

  /**
   * Wait for various conditions.
   */
  waitFor(opts?: {
    timeSec?: number;
    text?: string;
    textGone?: string;
    selector?: string;
    state?: string;
    timeout?: number;
    signal?: AbortSignal;
    timeoutMs?: number;
  }): Promise<void>;

  // ================ ASSERTIONS ================

  /**
   * Evaluate a deterministic assertion against the live page and return the result.
   *
   * Uses the same evaluator as cache-replay validation, so call-time and replay-time
   * verdicts agree for identical page state.
   *
   * Recording middleware persists this call as a `CachedAction` only when the
   * assertion passed. Failed assertions are not recorded (no cache pollution).
   *
   * Agentic / judgment-based assertions do NOT go through this method — those
   * are reported by the L3 agent via `updateWorkflowStep.assertionResults`.
   *
   * @param type - Type of assertion
   * @param condition - The text, selector, or JS expression to check
   * @param expected - Expected value for evaluate assertions (optional)
   */
  assert(
    type: AssertionType,
    condition: string,
    expected?: unknown,
    opts?: BrowserClientOptions & {
      strict?: boolean;
      severity?: AssertionSeverity;
      inlineAssertionId?: string;
      /** Human-readable label for the timeline entry title. */
      label?: string;
      /** Per-assertion product iframe hints, overriding the client's defaults. */
      frameHints?: string[];
      /** Ignore downloads captured before this step began. */
      downloadSinceMs?: number;
    }
  ): Promise<{
    passed: boolean;
    /** Structured when supported; older remote hosts omit it. */
    evaluationOutcome?: AssertionEvaluationOutcome;
    reason?: string;
  }>;

  /**
   * Optional hook for apps to register product-specific iframe selectors that
   * `assert()` should search in addition to the default generic list. Lets the
   * shared package stay app-agnostic while still supporting embedded frames.
   */
  setAssertionFrameHints?(hints: string[]): void;
  /** Current product-specific frame selectors used by `assert()`. */
  getAssertionFrameHints?(): string[];

  // ================ BROWSER MANAGEMENT ================

  /**
   * Close the current page/tab.
   */
  close(opts?: BrowserClientOptions): Promise<void>;

  /**
   * Resize the browser viewport.
   */
  resize(width: number, height: number, opts?: BrowserClientOptions): Promise<void>;

  /**
   * Manage browser tabs.
   * @param action - 'list' | 'new' | 'close' | 'select'
   * @param selector - Target for 'close'/'select': a positional index (back-compat)
   *   or `{ index?, handle?, match? }` where `handle` is a durable within-run tab
   *   id (from `tabs('list')`) and `match` is a URL/title substring predicate.
   */
  tabs(
    action: 'list' | 'new' | 'close' | 'select',
    selector?: BrowserTabSelector,
    opts?: BrowserClientOptions
  ): Promise<unknown>;

  /**
   * Enable/disable auto-capture of newly opened tabs (wire listeners without
   * switching the active page). Optional — only DirectPlaywrightClient implements it.
   */
  setAutoCaptureNewPages?(enabled: boolean): void;

  // ================ STORAGE ================

  /**
   * Get browser storage state (cookies + localStorage + IndexedDB).
   *
   * Returns Playwright's native storage state extended with an optional `indexedDB`
   * field containing serialized IndexedDB databases. This is critical for apps
   * that store auth tokens in IndexedDB (e.g., Firebase Auth).
   *
   * Shape: `{ cookies, origins, indexedDB?: IndexedDBData }`
   */
  getStorageState(opts?: {
    signal?: AbortSignal;
    timeoutMs?: number;
    method?: StorageStateMethod;
  }): Promise<unknown>;

  /**
   * Get the current page URL.
   */
  getCurrentUrl(opts?: BrowserClientOptions): Promise<string>;

  /**
   * Subscribe to main-frame navigation events. Fires for every URL change the
   * browser observes — full page loads, server redirects, SPA pushState/popstate
   * — not just explicit navigate() tool calls. Used by diagnostics to record a
   * timestamped URL history without per-step bookkeeping.
   *
   * Returns an unsubscribe function. Optional — clients without an event source
   * (mocks, batch clients) may omit it.
   */
  onNavigation?(handler: (url: string) => void): () => void;

  /**
   * Get and clear recovery info if a crash recovery happened recently.
   * Returns recovery info once, then null on subsequent calls until next crash.
   * Used by browser tools to prepend recovery notice to tool results.
   *
   * Optional - only supported by DirectPlaywrightClient.
   */
  consumeRecoveryInfo?(): RecoveryInfo | null;

  /**
   * Get the current viewport dimensions.
   * Returns the viewport size used when connecting, defaulting to 1280x800.
   */
  getViewportSize(): { width: number; height: number };

  /**
   * Get the current page title.
   */
  getTitle(opts?: BrowserClientOptions): Promise<string>;

  /**
   * Get all links on the current page.
   */
  getLinks(opts?: BrowserClientOptions): Promise<string[]>;

  // ================ DOWNLOADS ================

  /**
   * List all files downloaded during this browser session.
   * Returns a formatted string describing each captured download.
   */
  listDownloads(opts?: BrowserClientOptions): Promise<string>;

  /**
   * Read the content of a downloaded file.
   * For PDFs, extracts text content. For text formats, reads as UTF-8.
   * Binary files return metadata only.
   *
   * @param downloadId - ID of the download to read (defaults to most recent)
   */
  readDownload(
    downloadId?: number,
    opts?: BrowserClientOptions
  ): Promise<{
    filename: string;
    sizeBytes: number;
    mimeType: string;
    textContent: string | null;
  }>;

  /**
   * Fetch a PDF from an http(s) URL using the browser's authenticated session
   * (cookies) and extract its text content.
   *
   * Use this for PDFs that opened inline in the browser rather than downloading.
   * For captured file: downloads, use readDownload instead.
   *
   * @param url - The HTTP(S) PDF URL or browser-relative path
   */
  fetchPdfText(
    url: string,
    opts?: BrowserClientOptions
  ): Promise<{ text: string; totalPages: number; sizeBytes: number }>;

  /**
   * Fetch a PDF using the browser's authenticated context (cookies) and return
   * it as a base64 `application/pdf` payload for native document vision.
   *
   * Use this when a PDF opened inline (Chromium renders it to a plugin surface
   * the DOM/snapshot cannot read) and its layout matters or text extraction is
   * ambiguous. Fails for PDFs larger than the provider document limit.
   *
   * @param url - The HTTP(S) PDF URL or browser-relative path
   */
  fetchPdfImage(
    url: string,
    opts?: BrowserClientOptions
  ): Promise<{ base64: string; mediaType: string; sizeBytes: number }>;

  /**
   * Return all downloads captured during this browser session.
   * Used by the execution pipeline to persist download artifacts to S3.
   */
  getCapturedDownloads(): CapturedDownload[];

  /**
   * Get toast notifications captured from the page via MutationObserver.
   * Returns the current list of active toasts (with TTL tracking).
   *
   * Optional — only supported by DirectPlaywrightClient.
   */
  getCapturedToasts?(): CapturedToast[];

  /**
   * Get NEW console error messages since the last snapshot.
   * Returns errors and advances the internal cursor so they only appear once.
   *
   * Optional — only supported by DirectPlaywrightClient.
   */
  getNewConsoleErrors?(): CapturedConsoleMessage[];

  /**
   * Get NEW failed network requests (4xx/5xx) since the last snapshot.
   * Returns failures and marks them as seen so they only appear once.
   *
   * Optional — only supported by DirectPlaywrightClient.
   */
  getNewFailedRequests?(): CapturedNetworkRequest[];

  /**
   * Write a snapshot YAML to disk for bash tool analysis.
   * Files are tracked and cleaned up when disconnect() is called.
   * Optional - only supported by DirectPlaywrightClient.
   * @param yaml - The raw accessibility tree YAML content
   * @returns Path to the written file
   */
  writeSnapshotToDisk?(yaml: string): Promise<string>;

  /**
   * Get the bounding box of an element by its ref.
   * Returns null if element not found or not visible.
   */
  getElementBoundingBox(
    ref: string,
    opts?: BrowserClientOptions
  ): Promise<{ x: number; y: number; width: number; height: number } | null>;

  /**
   * Batch-query bounding boxes for multiple element refs in a single round-trip.
   * Returns a Map from ref to bounding box (missing refs are omitted).
   */
  getElementBoundingBoxes?(
    refs: string[],
    opts?: BrowserClientOptions
  ): Promise<Map<string, { x: number; y: number; width: number; height: number }>>;

  /**
   * Probe what element is at a given pixel coordinate.
   * Returns element metadata, bounding box, and matched ref from the current snapshot.
   * Returns null if no element found or probe fails.
   */
  probeAtCoordinates?(
    x: number,
    y: number,
    opts?: BrowserClientOptions
  ): Promise<ProbeResult | null>;

  // ================ TRACING ================

  /**
   * Start recording a trace.
   */
  startTracing(opts?: BrowserClientOptions): Promise<void>;

  /**
   * Stop recording and get trace file paths.
   *
   * `opts.upload` is a presigned S3 PUT the worker mints just-in-time (see
   * {@link ArtifactUploadTarget}). Local implementations (`DirectPlaywrightClient`)
   * ignore it and behave exactly as if it were absent — the resulting `trace`
   * path is already readable by the caller. A guest that DOES honor it (the
   * MicroVM guest's dispatch executor) merges its trace chunks into one
   * archive, PUTs it to `opts.upload.url`, and returns `s3Key`/`sizeBytes`
   * instead of (or alongside) a locally-readable `trace` path.
   */
  stopTracing(opts?: BrowserClientOptions & { upload?: ArtifactUploadTarget }): Promise<{
    trace: string;
    /**
     * All captured trace chunks, in order. One per browser context — a session
     * with a re-auth context swap produces more than one. `trace` is `traces[0]`
     * for back-compat; callers that want the whole session should merge `traces`
     * (see `mergeTraceArchives` in @chatsdet/trace-tools). Optional: clients that
     * don't chunk (MCP, remote shims) omit it; consumers fall back to `[trace]`.
     */
    traces?: string[];
    network: string;
    resources: string;
    directory: string | null;
    legend: string | null;
    /**
     * Set when `opts.upload` was honored — the implementation merged its trace
     * chunks and uploaded the archive itself. When present, callers should
     * skip local merge/upload entirely and just tag this key for retention.
     */
    s3Key?: string;
    /** Uploaded byte size — only meaningful alongside `s3Key`. */
    sizeBytes?: number;
  }>;

  /**
   * Open a named group in the trace timeline. Actions between group() and
   * groupEnd() appear as a collapsible section in the Playwright trace viewer.
   * No-op when tracing is not active.
   */
  traceGroup(name: string): Promise<void>;

  /**
   * Close the current trace group opened by traceGroup().
   * No-op when tracing is not active.
   */
  traceGroupEnd(): Promise<void>;

  /**
   * Return the locally tracked count of open Playwright trace groups.
   * Playwright closes groups with stack semantics, so callers that open a
   * scoped group should restore to their starting depth instead of closing
   * exactly one group.
   */
  traceGroupDepth(): number;

  /**
   * Close trace groups until the locally tracked depth is at or below `depth`.
   * Implementations must cap the close loop so tracing cleanup cannot spin
   * forever if local bookkeeping and the browser transport diverge.
   */
  traceGroupEndUntil(depth: number): Promise<void>;

  // ================ VIDEO RECORDING ================

  /**
   * Check if video recording is enabled for this session.
   * Video must be configured at connect() time via recordVideo option.
   */
  isVideoRecordingEnabled(): boolean;

  /**
   * Best-available wall-clock proxy for when the browser context was created
   * (and therefore when the video encoder started).
   * Returns null if context hasn't been created yet.
   */
  getContextCreatedAtMs?(): number | null;

  /**
   * Get the wall-clock timestamp when the screencast encoder started recording.
   * More accurate than getContextCreatedAtMs() for screencast mode.
   */
  getRecordingStartedAtMs?(): number | null;

  /**
   * Create the screencast encoder if it doesn't exist yet (screencast mode only).
   * BrowserSession normally calls this at its logical recording boundary. Remote
   * transports may have already started their encoder while installing a
   * video-capable context, so this is not a guarantee that earlier footage is absent.
   */
  ensureScreencastEncoder?(): void;

  /**
   * Finalize video recording and save to disk.
   * This closes the page to complete the video file, then saves it.
   *
   * IMPORTANT: Must be called BEFORE disconnect() to capture the video.
   * After calling this, the page will be closed but browser context remains.
   *
   * `opts.upload` is a presigned S3 PUT the worker mints just-in-time (see
   * {@link ArtifactUploadTarget}). Local implementations ignore it. A guest
   * that honors it PUTs the finished mp4 to `opts.upload.url` and returns
   * `s3Key`/`sizeBytes`/`durationMs` on the result instead of a locally
   * readable `videoPath`.
   *
   * @returns Video path and metadata, or null if recording wasn't enabled
   */
  saveVideo(opts?: { upload?: ArtifactUploadTarget }): Promise<VideoRecordingResult | null>;

  /**
   * Get the path to the recorded video file.
   * Returns the cached path from a previous saveVideo() call.
   *
   * @returns Video path and metadata, or null if video wasn't saved
   */
  getVideoPath(): Promise<VideoRecordingResult | null>;

  // ================ PAGE ACCESS (for cache replay) ================

  /**
   * Get the underlying Playwright Page for direct manipulation.
   * Used by cache replay to execute actions directly without going through the agent.
   *
   * Note: This is optional and only supported by DirectPlaywrightClient.
   * Wrapper clients should proxy this to their underlying client.
   *
   * @returns The Playwright Page object or null if not available
   */
  getPageForReplay?(): Promise<unknown>;

  // ================ CACHE METADATA ================

  /**
   * Get cached element metadata from the last resolveRef call.
   * Used by the recording layer to derive selectors without re-parsing YAML snapshots.
   *
   * When the browser client resolves an aria-ref to a locator, it can extract
   * DOM properties (role, aria-label, text content, parent context) directly from
   * the element. This is more accurate than parsing the YAML snapshot because:
   * - YAML accessible names can concatenate text from child elements
   * - Direct DOM access gets the actual aria-label, textContent, etc.
   *
   * Note: This is optional and only supported by DirectPlaywrightClient.
   * Wrapper clients should proxy this to their underlying client.
   *
   * @param ref - The element ref (e.g., 'e5')
   * @returns Cached ElementInfo or undefined if not available
   */
  getLastResolvedMetadata?(ref: string): ElementInfo | undefined;

  // ================ CDP SCREENCAST ================

  /**
   * Start CDP screencast for continuous frame streaming.
   * Frames are delivered as data URLs to the handler, throttled by options.minIntervalMs.
   *
   * Optional — only supported by DirectPlaywrightClient.
   */
  startScreencast?(
    onFrame: ScreencastFrameHandler,
    options?: ScreencastOptions,
    videoCallbacks?: VideoEncodingCallbacks
  ): Promise<void>;

  /**
   * Stop CDP screencast.
   * Safe to call if already stopped or never started.
   *
   * Optional — only supported by DirectPlaywrightClient.
   */
  stopScreencast?(): Promise<void>;

  /**
   * Whether CDP screencast is currently active.
   *
   * Optional — only supported by DirectPlaywrightClient.
   */
  isScreencastActive?(): boolean;

  // ================ NETWORK OBSERVABILITY ================

  /**
   * Get info about currently pending (in-flight) network requests.
   * Used by semantic diff to surface loading state to the agent.
   *
   * Optional — only supported by DirectPlaywrightClient.
   */
  getPendingNetworkInfo?(): { pendingCount: number; sampleUrls: string[] };

  /**
   * Wait for the network to become idle (no in-flight requests for 500ms).
   * Returns immediately when already idle, so zero overhead for simple actions.
   *
   * Optional — only supported by DirectPlaywrightClient and RemoteBrowserClient.
   */
  waitForNetworkIdle?(timeoutMs?: number): Promise<void>;

  /**
   * Consume and return any pending native browser dialog message (alert/confirm/prompt).
   * Native dialogs don't appear in the accessibility tree, so they must be surfaced separately.
   * Returns null if no dialog is pending.
   *
   * Optional — only supported by DirectPlaywrightClient.
   */
  consumePendingDialogMessage?(): string | null;

  /**
   * Get the cursor overlay instance for animating a visible cursor in video recordings.
   * Used by cache replay to animate the cursor when bypassing PlaywrightClient action methods.
   *
   * Optional — only supported by DirectPlaywrightClient.
   */
  getCursorOverlay?(): CursorOverlay;

  /**
   * Get the autonomous (event-driven) cursor used by the fast-drive `run_code`
   * path. Used by cache replay so code-backed actions animate the same on-brand
   * cursor. Optional — only supported by DirectPlaywrightClient.
   */
  getAutonomousCursor?(): AutonomousCursor;

  // ================ HTTP REQUESTS ================

  /**
   * Make an HTTP request using the browser's authenticated context (cookies, session).
   * Uses Playwright's `context.request` API which inherits browser cookies automatically.
   *
   * Optional — only supported by DirectPlaywrightClient.
   */
  fetch?(options: {
    url: string;
    method?: string;
    headers?: Record<string, string>;
    body?: string;
    /** Per-request timeout in ms. Falls back to Playwright's 30s default if omitted. */
    timeoutMs?: number;
    /** Parent cancellation signal for workflow/job aborts. */
    signal?: AbortSignal;
  }): Promise<{
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: string | null;
  }>;

  /**
   * Begin an isolated request session for a sequence of authenticated API calls
   * (e.g. an api_sequence node). While active, {@link fetch} uses a native
   * request path seeded from the live session that self-heals when a response
   * rotates cookies. In-memory only; nothing is persisted. Pair with
   * {@link endApiRequestSession}.
   *
   * Optional — only supported by DirectPlaywrightClient.
   */
  beginApiRequestSession?(): Promise<void>;

  /**
   * End the isolated request session started by {@link beginApiRequestSession}
   * and drop its in-memory cookie state. Idempotent.
   *
   * Optional — only supported by DirectPlaywrightClient.
   */
  endApiRequestSession?(): Promise<void>;

  // ================ EMULATION ROUTES ================

  /**
   * Set up request interception routes on the browser context.
   * Routes persist across page navigations and apply to all pages in the context
   * (including popups), making this ideal for intercepting OAuth redirects.
   *
   * Routes are stored as a GROUP under `key` (default `'default'`): a call
   * replaces only its own group, never another owner's (CF Access header
   * injection, emulation, and the diagnostic host boundary are independent
   * owners), and every stored group is re-armed together on context
   * recreation (crash recovery, context swap). An empty `routes` array disarms
   * the group.
   *
   * `opts.matchFirst` registers the group last so Playwright's newest-first
   * matching consults it before every other group (its pass-throughs must use
   * `fallback()`); `opts.reapplyOnContextSwap: false` marks credential-bound
   * groups (secret headers) that must not be replayed onto a swapped context.
   *
   * Optional — only supported by DirectPlaywrightClient.
   */
  setupContextRoutes?(
    routes: ContextRoute[],
    key?: string,
    opts?: { matchFirst?: boolean; reapplyOnContextSwap?: boolean }
  ): Promise<void>;

  /**
   * Arm an egress policy over every model-authored `run_code` block: the
   * injected worker-side `fetch` binding and `page.goto` refuse non-http(s)
   * and off-host targets, and `page.context()` is denied. Pass `null` to
   * disarm. Optional — only supported by DirectPlaywrightClient.
   */
  setRunCodeEgressPolicy?(policy: { allowedHosts: string[] } | null): void;

  /**
   * Best-effort: dispatch `window.stop()` on the active page to release any
   * pending fetch/XHR/resource loads that may be wedging the JS context.
   * Fire-and-forget, idempotent, capped internally to ~1s.
   *
   * Called by `BrowserToolExecutor.withCallSignal` when the per-tool timeout
   * fires, so the next tool call sees an unwedged page even if the previous
   * action's orphan Playwright op is still settling.
   *
   * Optional. Implemented by `DirectPlaywrightClient`. Mocks and
   * `RemoteBrowserClient` leave this undefined; the executor's abort listener
   * skips via `typeof === 'function'` guard.
   */
  attemptUnwedgePage?(): void;

  /**
   * Cheap, driver-bounded check of whether the active page's main thread can
   * service a trivial evaluate. Used by the per-tool-timeout recovery path to
   * decide whether to force-recover a wedged page vs. spare a slow-but-alive
   * one. Returns `'alive' | 'unresponsive' | 'closed'`. Never throws.
   *
   * Optional. Implemented by `DirectPlaywrightClient` only.
   */
  probeLiveness?(): Promise<'alive' | 'unresponsive' | 'closed'>;

  /**
   * Force-recover the active page: close it (killing every in-flight/orphaned
   * Playwright op on it) and recreate it, re-navigating to the last known URL.
   * The backstop for the Playwright cancellation gap — `ariaSnapshot`/
   * `screenshot` have no timeout and orphan on a wedged renderer; closing the
   * page is the only thing that truly stops them. Returns whether recovery
   * succeeded. Never throws.
   *
   * Optional. Implemented by `DirectPlaywrightClient` only.
   */
  forceRecoverPage?(reason: string): Promise<boolean>;
}

/**
 * A route for context-level request interception.
 * Used by the emulation middleware to intercept OAuth redirects and other
 * browser-level traffic to external services.
 */
export interface ContextRoute {
  /** URL pattern (glob or regex) for Playwright context.route() */
  urlPattern: string | RegExp;
  /** Handler receives a minimal route handle for fulfilling or continuing */
  handler: (route: ContextRouteHandle) => Promise<void>;
}

/**
 * Minimal abstraction over Playwright's Route for context-level interception.
 */
export interface ContextRouteHandle {
  /** Fulfill the request with a custom response */
  fulfill(opts: {
    status?: number;
    headers?: Record<string, string>;
    body?: string;
  }): Promise<void>;
  /**
   * Continue the request to the real server, optionally overriding headers.
   * When `headers` is supplied it REPLACES the request headers, so callers that
   * only want to add headers must merge onto `request().headers()` first.
   */
  continue(opts?: { headers?: Record<string, string> }): Promise<void>;
  /** Access the intercepted request */
  request(): {
    url(): string;
    method(): string;
    postData(): string | null;
    /** Current request headers (lowercased keys), for merge-on-continue. */
    headers(): Record<string, string>;
  };
  /**
   * True when the intercepted request is a top-frame document navigation
   * (`request.isNavigationRequest()` with no parent frame). Required so
   * host-boundary guards can never silently no-op against a handle that cannot
   * classify: a navigation whose frame cannot be inspected reports `true`
   * (fail closed), a request that is not a navigation reports `false`.
   */
  isMainFrameNavigation(): boolean;
  /**
   * Hand the request to the NEXT matching route handler instead of finalizing
   * it (Playwright `route.fallback()`). Pass-through guards should prefer this
   * over `continue()` so they cannot swallow another owner's handler on the
   * same URL. Optional — fakes without it fall back to `continue()`.
   */
  fallback?(): Promise<void>;
}
